import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  User, 
  Phone, 
  MapPin, 
  Home,
  Upload,
  Calendar,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicFormPage() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClosed, setIsClosed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  
  // Form responses
  const [responses, setResponses] = useState({});
  const [files, setFiles] = useState({});
  
  // ITS lookup state
  const [itsLookupLoading, setItsLookupLoading] = useState(false);
  const [itsData, setItsData] = useState(null);
  const [itsError, setItsError] = useState(null);
  
  // Validation errors
  const [errors, setErrors] = useState({});

  const [loadError, setLoadError] = useState(false);

  const fetchForm = useCallback(async () => {
    try {
      setLoadError(false);
      const response = await axios.get(`${API_URL}/api/public/forms/${formId}`);
      setForm(response.data.form);
      setIsClosed(response.data.is_closed);
    } catch (error) {
      console.error('Failed to load form:', error?.message, error?.response?.status);
      setLoadError(true);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  // Handle ITS lookup
  const handleItsLookup = async (itsValue) => {
    if (!itsValue || itsValue.length < 8) {
      setItsData(null);
      setItsError(null);
      return;
    }
    
    setItsLookupLoading(true);
    setItsError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/forms/its-lookup/${itsValue}`);
      if (response.data.found) {
        setItsData(response.data.data);
        // Auto-fill responses for auto-fetched fields
        setResponses(prev => ({
          ...prev,
          auto_full_name: response.data.data.full_name,
          auto_hof_name: response.data.data.hof_name,
          auto_mobile: response.data.data.mobile,
          auto_mohalla: response.data.data.mohalla,
          auto_sector: response.data.data.sector,
          auto_address: response.data.data.address
        }));
      } else {
        setItsData(null);
        setItsError('ITS not found in database');
      }
    } catch (error) {
      setItsError('Failed to lookup ITS');
    } finally {
      setItsLookupLoading(false);
    }
  };

  const handleInputChange = (fieldId, value, fieldType) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    
    // Clear error when user types
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
    
    // Trigger ITS lookup
    if (fieldType === 'its_number' || fieldType === 'hof_its_number') {
      handleItsLookup(value);
    }
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    const currentValues = responses[fieldId] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, option];
    } else {
      newValues = currentValues.filter(v => v !== option);
    }
    
    setResponses(prev => ({ ...prev, [fieldId]: newValues }));
  };

  const handleFileChange = async (fieldId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFiles(prev => ({ ...prev, [fieldId]: reader.result }));
      setResponses(prev => ({ ...prev, [fieldId]: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};
    
    form.fields.forEach(field => {
      if (field.required) {
        const value = responses[field.id];
        
        if (field.type === 'checkbox') {
          if (!value || value.length === 0) {
            newErrors[field.id] = 'Please select at least one option';
          }
        } else if (field.type === 'photo') {
          if (!files[field.id]) {
            newErrors[field.id] = 'Please upload a photo';
          }
        } else if (field.type === 'its_number' || field.type === 'hof_its_number') {
          // ITS must be valid (found in database)
          if (!value || (typeof value === 'string' && !value.trim())) {
            newErrors[field.id] = 'This field is required';
          } else if (itsError) {
            newErrors[field.id] = 'Please enter a valid ITS number';
          } else if (!itsData && value && value.length === 8) {
            newErrors[field.id] = 'ITS not found - please verify the number';
          }
        } else {
          if (!value || (typeof value === 'string' && !value.trim())) {
            newErrors[field.id] = 'This field is required';
          }
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submissionData = {
        form_id: formId,
        responses: { ...responses },
        files: Object.keys(files).length > 0 ? files : null
      };
      
      // Add auto-fetched data to responses if ITS was looked up
      if (itsData) {
        submissionData.responses.auto_full_name = itsData.full_name;
        submissionData.responses.auto_hof_name = itsData.hof_name;
        submissionData.responses.auto_mobile = itsData.mobile;
        submissionData.responses.auto_mohalla = itsData.mohalla;
        submissionData.responses.auto_sector = itsData.sector;
        submissionData.responses.auto_address = itsData.address;
      }
      
      const response = await axios.post(`${API_URL}/api/public/forms/${formId}/submit`, submissionData);
      
      setSubmitted(true);
      setSubmissionId(response.data.submission_id);
      toast.success('Form submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit form';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.id];
    
    switch (field.type) {
      case 'short_text':
        return (
          <Input
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
            placeholder={field.placeholder || ''}
            className={error ? 'border-red-500' : ''}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'long_text':
        return (
          <Textarea
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
            placeholder={field.placeholder || ''}
            rows={4}
            className={error ? 'border-red-500' : ''}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
            placeholder={field.placeholder || ''}
            className={error ? 'border-red-500' : ''}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'dropdown':
        return (
          <Select
            value={responses[field.id] || ''}
            onValueChange={(value) => handleInputChange(field.id, value, field.type)}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''} data-testid={`field-${field.id}`}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map(option => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${option}`}
                  checked={(responses[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => handleCheckboxChange(field.id, option, checked)}
                  data-testid={`field-${field.id}-${option}`}
                />
                <label 
                  htmlFor={`${field.id}-${option}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
            className={error ? 'border-red-500' : ''}
            data-testid={`field-${field.id}`}
          />
        );
      
      case 'photo':
        return (
          <div>
            <div className="flex items-center gap-4">
              <label 
                htmlFor={`file-${field.id}`}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${
                  error ? 'border-red-500' : 'border-slate-200'
                }`}
              >
                <Upload className="w-4 h-4 text-slate-500" />
                <span className="text-sm">{files[field.id] ? 'Change Photo' : 'Upload Photo'}</span>
              </label>
              <input
                type="file"
                id={`file-${field.id}`}
                accept="image/*"
                onChange={(e) => handleFileChange(field.id, e)}
                className="hidden"
                data-testid={`field-${field.id}`}
              />
              {responses[field.id] && (
                <span className="text-sm text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  {responses[field.id]}
                </span>
              )}
            </div>
            {files[field.id] && (
              <div className="mt-2">
                <img 
                  src={files[field.id]} 
                  alt="Preview" 
                  className="max-w-xs max-h-32 rounded border"
                />
              </div>
            )}
          </div>
        );
      
      case 'its_number':
      case 'hof_its_number':
        return (
          <div className="space-y-3">
            <div className="relative">
              <Input
                value={responses[field.id] || ''}
                onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
                placeholder="Enter 8-digit ITS number"
                maxLength={8}
                className={error ? 'border-red-500' : ''}
                data-testid={`field-${field.id}`}
              />
              {itsLookupLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
              )}
            </div>
            
            {itsError && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {itsError}
              </p>
            )}
            
            {itsData && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-700 font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Member Found
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Name:</span>
                    <span className="font-medium">{itsData.full_name}</span>
                  </div>
                  {itsData.hof_name && itsData.hof_name !== itsData.full_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">HOF:</span>
                      <span className="font-medium">{itsData.hof_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Mobile:</span>
                    <span className="font-medium">{itsData.mobile || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Mohalla:</span>
                    <span className="font-medium">{itsData.mohalla || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Sector:</span>
                    <span className="font-medium">{itsData.sector || 'N/A'}</span>
                  </div>
                  {itsData.age && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Age:</span>
                      <span className="font-medium">{itsData.age} years</span>
                    </div>
                  )}
                  {itsData.gender && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Gender:</span>
                      <span className="font-medium">{itsData.gender}</span>
                    </div>
                  )}
                  {itsData.address && (
                    <div className="flex items-start gap-2 col-span-full">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span className="text-slate-500">Address:</span>
                      <span className="font-medium">{itsData.address}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'rating':
        const config = field.rating_config || { min: 1, max: 5, step: 1, left_label: '', right_label: '' };
        const ratingValues = [];
        for (let v = config.min; v <= config.max; v += config.step) {
          ratingValues.push(v);
        }
        const selectedRating = responses[field.id];
        
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-500 min-w-[100px] text-left">{config.left_label}</span>
              <div className="flex gap-2 flex-wrap justify-center">
                {ratingValues.map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleInputChange(field.id, value, field.type)}
                    className={`w-10 h-10 rounded-full border-2 text-sm font-semibold transition-all ${
                      selectedRating === value
                        ? 'border-navy bg-navy text-white'
                        : 'border-slate-300 text-slate-600 hover:border-navy hover:bg-navy/10'
                    } ${error ? 'border-red-500' : ''}`}
                    data-testid={`field-${field.id}-${value}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <span className="text-sm text-slate-500 min-w-[100px] text-right">{config.right_label}</span>
            </div>
            {selectedRating && (
              <p className="text-center text-sm text-navy font-medium">
                Selected: {selectedRating}
              </p>
            )}
          </div>
        );
      
      default:
        return (
          <Input
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value, field.type)}
            placeholder={field.placeholder || ''}
            className={error ? 'border-red-500' : ''}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800">Form Not Found</h2>
            <p className="text-slate-500 mt-2">
              {loadError 
                ? 'Unable to load this form. Please check your internet connection and try again.' 
                : 'This form does not exist or has been deleted.'}
            </p>
            {loadError && (
              <Button 
                onClick={() => { setLoading(true); fetchForm(); }} 
                className="mt-4 bg-navy hover:bg-navy/90"
                data-testid="retry-load-form"
              >
                Retry
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isClosed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800">Form Closed</h2>
            <p className="text-slate-500 mt-2">This form is currently not accepting submissions.</p>
            <p className="text-sm text-slate-400 mt-4">{form.name}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800">Thank You!</h2>
            <p className="text-slate-500 mt-2">Your response has been submitted successfully.</p>
            <p className="text-xs text-slate-400 mt-4">Reference: {submissionId}</p>
            
            {form.allow_multiple_submissions && (
              <Button 
                variant="outline" 
                className="mt-6"
                onClick={() => {
                  setSubmitted(false);
                  setResponses({});
                  setFiles({});
                  setItsData(null);
                }}
              >
                Submit Another Response
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-form-page">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          <img 
            src="/logo.jpeg" 
            alt="Logo" 
            className="w-16 h-16 rounded-lg mx-auto mb-4 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-2xl font-bold text-slate-800">{form.name}</h1>
          {form.description && (
            <p className="text-slate-500 mt-2">{form.description}</p>
          )}
        </div>

        {/* Form */}
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {form.fields?.sort((a, b) => a.order - b.order).map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  {field.description && (
                    <p className="text-xs text-slate-500">{field.description}</p>
                  )}
                  {renderField(field)}
                  {errors[field.id] && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors[field.id]}
                    </p>
                  )}
                </div>
              ))}
              
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-navy hover:bg-navy/90"
                  disabled={submitting}
                  data-testid="submit-form-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Anjuman E Saifee Management System
        </p>
      </div>
    </div>
  );
}
